/**
 * In-browser API shim for the managed-theater demo (no backend required).
 */

import { DEMO_USER, getActivity, getAttention, getDashboardStats, getLead, getLiveState, getSequences, getSettings, getTeam, getTcpa, listLeads, patchLeadLocal } from './mockStore.js';

export const isDemoMode = true;

type Result<T> = { data: T | null; status: number; ok: boolean };

function ok<T>(data: T): Result<T> {
  return { data, status: 200, ok: true };
}

function notFound<T = never>(): Result<T> {
  return { data: null, status: 404, ok: false };
}

function forbidden<T = never>(message: string): Result<T> {
  return { data: { error: message } as T, status: 403, ok: false };
}

/** Capability snapshot for mutation gates (set by PortalProvider). */
let crmUnlocked = false;

export function setDemoCrmUnlocked(v: boolean) {
  crmUnlocked = v;
}

export async function demoRequest<T>(
  method: string,
  path: string,
  body?: unknown,
): Promise<Result<T>> {
  // strip query
  const [pathname, qs] = path.split('?');
  const params = new URLSearchParams(qs ?? '');

  // Auth
  if (pathname === '/me' && method === 'GET') return ok(DEMO_USER as T);
  if (pathname === '/auth/login' && method === 'POST') return ok(DEMO_USER as T);
  if (pathname === '/auth/logout' && method === 'POST') return ok({} as T);

  // Settings
  if (pathname === '/api/settings' && method === 'GET') return ok(getSettings() as T);
  if (pathname === '/api/settings' && method === 'PATCH') {
    // Allow only “account” style patches in managed mode; CRM still limited
    const b = (body ?? {}) as Record<string, unknown>;
    const allowed = ['timezone', 'notifications'];
    const keys = Object.keys(b);
    const onlyAllowed = keys.every((k) => allowed.includes(k) || k === 'aiName');
    if (!onlyAllowed && !crmUnlocked) {
      return forbidden('Managed by ContactRate — factory settings are view-only.');
    }
    return ok({ ...getSettings(), ...b } as T);
  }

  // Team
  if (pathname === '/api/team' && method === 'GET') return ok(getTeam() as T);
  if (pathname.startsWith('/api/team/') && method === 'PATCH') {
    // Allow availability / phone updates (routing)
    return ok({ ...(getTeam()[0] ?? {}), ...(body as object) } as T);
  }

  // TCPA
  if (pathname === '/api/tcpa' && method === 'GET') return ok(getTcpa() as T);
  if (pathname.includes('/override') && method === 'PATCH') {
    return forbidden('Compliance is managed by ContactRate.');
  }

  // Leads
  if (pathname === '/api/leads' && method === 'GET') {
    return ok(
      listLeads({
        stage: params.get('stage') ?? undefined,
        source: params.get('source') ?? undefined,
        search: params.get('search') ?? undefined,
        page: params.get('page') ? Number(params.get('page')) : 1,
        pageSize: params.get('pageSize') ? Number(params.get('pageSize')) : 50,
      }) as T,
    );
  }
  if (pathname.startsWith('/api/leads/') && method === 'GET') {
    const id = pathname.split('/').pop()!;
    const lead = getLead(id);
    return lead ? ok(lead as T) : notFound();
  }
  if (pathname.startsWith('/api/leads/') && method === 'PATCH') {
    if (!crmUnlocked) {
      return forbidden('Pipeline & lead edits unlock with Workspace CRM.');
    }
    const id = pathname.split('/').pop()!;
    return ok(patchLeadLocal(id, (body ?? {}) as Record<string, unknown>) as T);
  }

  // Read models
  if (pathname === '/api/dashboard/stats' && method === 'GET') {
    return ok(getDashboardStats(params.get('range') ?? 'today') as T);
  }
  if (pathname === '/api/live/state' && method === 'GET') return ok(getLiveState() as T);
  if (pathname === '/api/activity' && method === 'GET') return ok(getActivity() as T);
  if (pathname === '/api/escalations/attention' && method === 'GET') return ok(getAttention() as T);
  if (pathname === '/api/sequences' && method === 'GET') return ok(getSequences() as T);

  // Thread feedback — view theater, no write in managed
  if (pathname.includes('/feedback') && method === 'PATCH') {
    if (!crmUnlocked) return forbidden('Feedback is operator-side in managed mode.');
    return ok({ id: 'x', feedback: (body as { value?: string })?.value ?? null } as T);
  }

  // Fallback
  console.warn('[demoApi] unhandled', method, path);
  return ok({} as T);
}
